import { Button, Icon, IconButton } from "@material-ui/core";
import { CheckCircleRounded } from "@material-ui/icons";
import firebase from "firebase/compat/app";
import CalendarRange from "mdi-material-ui/CalendarRange";
import CloudSearch from "mdi-material-ui/CloudSearch";
import SortAlphabeticalVariant from "mdi-material-ui/SortAlphabeticalVariant";
import Update from "mdi-material-ui/Update";
import React, { Component } from "react";
import styled from "styled-components";

import {
    LibraryView,
    PresentationFilters,
    SearchThrottleWait
} from "common/constants";
import { ActionPermissionsObject, PermissionActionType, WorkspacePlanTier, WorkspaceResourceType, WorkspaceUserRole } from "common/interfaces";
import { getStaticUrl } from "js/config";
import PresentationLibraryController, { PresentationFilterType, PresentationLibraryControllerState, PresentationLibraryFilter, PresentationLibraryPresentation, PresentationLibrarySort } from "js/controllers/PresentationLibraryController";
import WorkspaceController, { WorkspaceControllerState } from "js/controllers/WorkspaceController";
import AppController from "js/core/AppController";
import getLogger, { LogGroup } from "js/core/logger";
import { User } from "js/core/models/user";
import { OnMountedReporter } from "js/core/utilities/useMountedAt";
import withProgressDialog from "js/core/utilities/withProgressDialog";
import { CreatePresentationDialogPaneType } from "js/editor/CreatePresentation/CreatePresentationDialogTypes";
import ShortcutPanel from "js/editor/PresentationEditor/Components/ShortCutPanel";
import { PresentationLibraryGrid } from "js/editor/PresentationLibrary/PresentationLibraryGrid";
import { PresentationLibraryList } from "js/editor/PresentationLibrary/PresentationLibraryList";
import { PresentationLibrarySideBar } from "js/editor/PresentationLibrary/PresentationLibrarySideBar";
import { PresentationLibraryViewSelect } from "js/editor/PresentationLibrary/PresentationLibraryViewSelect";
import { PresentationListHeader } from "js/editor/PresentationLibrary/PresentationListHeader";
import { app } from "js/namespaces";
import {
    ShowConfirmationDialog,
    ShowErrorDialog
} from "js/react/components/Dialogs/BaseDialog";
import FetchingClickShield from "js/react/components/FetchingClickShield";
import { FlexSpacer, Gap10, Gap20, Gap30, Gap5 } from "js/react/components/Gap";
import { FlexBox, ScrollBox } from "js/react/components/LayoutGrid";
import { NoMatchNotice, Notice, ResourceNotice } from "js/react/components/Notice";
import Spinner from "js/react/components/Spinner";
import {
    StyledWarningIcon,
    UIPane,
    UIPaneContents,
    UIPaneSideBar,
    UIPaneWithSideBar,
    WarningBox,
    WarningButton
} from "js/react/components/UiComponents";
import { themeColors } from "js/react/sharedStyles";
import { withFirebaseUser } from "js/react/views/Auth/FirebaseUserContext";
import { _ } from "js/vendor";
import LibrarySkeleton from "js/editor/PresentationLibrary/components/LibrarySkeleton";

const logger = getLogger(LogGroup.LIBRARY);

const CloseButtonContainer = styled.div`
    text-align: right;
    margin-bottom: -15px;
    margin-top: 10px;
`;

const RemoteControlBanner = styled.div<{ blue?: boolean }>`
    padding: 10px;
    text-align: center;
    text-wrap: nowrap;
    background: ${props => props.blue ? themeColors.ui_blue : "orangered"};
    color: white;
    font-size: 18px;
    width: 100%;
    font-weight: 600;
`;

const SubFolderContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    flex-wrap: wrap;
    max-height: 108px;
    overflow-y: auto;
    margin-bottom: 10px;
    gap: 5px;
    row-gap: 5px;
`;

const SubFolder = styled.div<{ isSelected: boolean }>`
    background: ${props => props.isSelected ? "white" : "#e1e1e1"};
    padding: 10px 15px 10px 10px;
    font-weight: 600;
    font-size: 14px;
    display: flex;
    align-items: center;
    justify-content: flex-start;

    span {
        font-weight: 400;
    }

    .MuiIcon-root {
        opacity: 0.5;
    }

    &:hover {
        background: ${themeColors.selection};
    }
`;

const CreatePresentationButton = styled(Button)`
    flex-shrink: 0;
    height: 50px;
    background: ${themeColors.ui_blue} !important;
    padding: 0px 20px 0px 10px !important;

    .MuiButton-label {
        color: white;
        font-family: "Source Sans Pro";
        font-size: 16px;
        font-weight: 600;
        margin-left: 10px;
    }

    .MuiIcon-root {
        background: white;
        color: black;
        border-radius: 50%;
        padding: 3px;
        font-size: 17px;
        margin-right: 6px;
    }
`;

const CreatePresentationButtonIntegrator = styled(CreatePresentationButton)`
    border: 1px solid ${themeColors.ui_blue} !important;
    padding: 0 !important;
    margin-right: 14px !important;

    .MuiButton-label {
        color: #444;
        background: transparent;
        padding: 10px 20px;
    }

    .dark-mode & {
        .MuiButton-label {
            color: #ccc;
        }
    }
`;

export const Breadcrumb = styled.div`
    font-size: 18px;
    text-transform: uppercase;
    margin-right: 10px;
    color: #444;
    flex-shrink: 0;

    .dark-mode & {
        color: #ccc;
    }
`;

const StyledRoundedIcon = styled(CheckCircleRounded)`
    &.MuiSvgIcon-colorPrimary {
        width: 16px;
        height: 16px;
        margin-right: 8px;
    }
`;

type PresentationLibraryPaneProps = PresentationLibraryControllerState & WorkspaceControllerState & {
    workspaceId: string;
    firebaseUser: firebase.User;
    readOnly?: boolean;
    pane?: PresentationFilterType;
    folderId?: string;
    subFolderId?: string;
    user: typeof User;
    onSelectedPresentation: (presentationId: string) => void;
    onCloseDialog: () => void;
    showCloseIcon: boolean;
    showSidebar: boolean;
    onHideSidebar: () => void;
    onSwitchType: () => void;
    sideBarWidth: number;
    onResizeSidebar: () => void;
    className: string;
    showLibraryViewSelect: boolean;
    isMyPresentationsOnly: boolean;
    onReady?: () => void;
    onDoubleClick?: () => void;
}

interface InnerPresentationLibraryPaneState {
    draggedItems: PresentationLibraryPresentation[];
}

class InnerPresentationLibraryPane extends Component<PresentationLibraryPaneProps, InnerPresentationLibraryPaneState> {
    sideBarRef: React.RefObject<any>;
    scrollRef: React.RefObject<any>;

    handleSearchThrottled: (query: string) => void;

    get sortOptions() {
        const { searchQuery } = this.props;

        const options = [{
            value: "name", label: "Name", reverse: false, icon: <SortAlphabeticalVariant />
        }, {
            value: "createdAt", label: "Recently Created", reverse: true, icon: <CalendarRange />
        }, {
            value: "modifiedAt", label: "Recently Edited", reverse: true, icon: <Update />
        }];
        if (searchQuery) {
            options.push({
                value: "relevance", label: "Relevance", reverse: false, icon: <CloudSearch />
            });
        }
        return options;
    }

    constructor(props) {
        super(props);

        this.sideBarRef = React.createRef();
        this.scrollRef = React.createRef();

        this.handleSearchThrottled = _.throttle(this.handleSearch, SearchThrottleWait, { leading: false });

        this.state = {
            draggedItems: []
        };
    }

    componentWillUnmount() {
        PresentationLibraryController.setSearchQuery("");
    }

    handleSetViewType = (viewType: "grid" | "list") => {
        PresentationLibraryController.setViewType(viewType);
    }

    handleSearch = (query: string) => {
        PresentationLibraryController.setSearchQuery(query);
    }

    handleSort = (sort: PresentationLibrarySort) => {
        PresentationLibraryController.setSort(sort);
    }

    handleSetFilter = (filter: PresentationLibraryFilter) => {
        const { readOnly } = this.props;

        // if we are in read-only (We're adding a slide from My Slide sectio ) mode
        // we should just update the filter state because we
        // don't want to navigate away from the current page
        if (readOnly) {
            PresentationLibraryController.setFilter(filter);
        } else {
            AppController.showLibrary({ filter });
        }
    }

    handleSelectedPresentations = async (selection: PresentationLibraryPresentation[]) => {
        const { onSelectedPresentation } = this.props;

        if (selection.length === 0) {
            return;
        }

        if (onSelectedPresentation) {
            onSelectedPresentation(selection[0].id);
            return;
        }

        if (app.isConstrained) {
            await this.handleDoubleClickedPresentation(selection[0]);
        }
    }

    handleDoubleClickedPresentation = async (presentation: PresentationLibraryPresentation) => {
        if (this.props.onDoubleClick) {
            return this.props.onDoubleClick();
        }

        if (presentation.trashedAt) {
            return ShowErrorDialog({
                title: "Can't edit presentations in the trash",
                message: "Please restore this presentation back into your library before editing."
            });
        }

        // zoom app cases (meeting and non-meeting views)
        if (window.isZoomApp) {
            if (window.roomID && window.roomPresentationStarted) {
                return await window.roomPresentationStarted(presentation.id)
                    .catch(err => ShowErrorDialog({
                        title: "Error Starting Presentation",
                        message: err.message
                    }));
            }

            // @ts-ignore
            if (!window.roomID && window.roomPresentationPreviewed) {
                // @ts-ignore
                return await window.roomPresentationPreviewed(presentation.id)
                    .catch(err => ShowErrorDialog({
                        title: "Error Previewing Presentation",
                        message: err.message
                    }));
            }
        }

        // show player if in a Meeting Room
        if (window.roomID) {
            PresentationLibraryController.playPresentation(presentation.id);
            return;
        }

        // show player if user doesn't have write permissions or if the app is constrained mode
        if (!presentation.permissions.write || app.isConstrained) {
            PresentationLibraryController.playPresentation(presentation.id, !app.isConstrained);
            return;
        }

        // show presentation editor
        AppController.showEditor({ presentationId: presentation.id });
    }

    handleStartDragItems = (selection: PresentationLibraryPresentation[]) => {
        this.setState({ draggedItems: selection });
    }

    handleDropDragItems = () => {
        this.setState({ draggedItems: [] });
    }

    handleSubFolderMouseUp = async (subFolderId: string) => {
        const { filter } = this.props;
        const { draggedItems } = this.state;

        if (draggedItems?.length) {
            const resources = draggedItems.map(presentation => ({
                resourceId: presentation.id,
                resourceType: WorkspaceResourceType.PRESENTATION,
                permissions: { read: { object: true, permissions: true }, write: { object: true, permissions: true } }
            }));

            try {
                await withProgressDialog({
                    title: `Adding ${resources.length} presentation${resources.length === 1 ? "" : "s"} to folder...`,
                    action: async () => {
                        await WorkspaceController.updateWorkspaceResourceFolderResources({
                            folderId: subFolderId,
                            update: {
                                add: resources,
                            }
                        });

                        this.handleSetFilter({
                            type: PresentationFilterType.TEAM_FOLDER,
                            folderId: filter.folderId,
                            subFolderId
                        });
                    }
                });
            } catch (err) {
                logger.error(err, "Error adding presentations to folder");

                ShowErrorDialog({
                    title: "Error adding presentations to folder",
                    message: err.message
                });
            }
        } else {
            this.handleSetFilter({
                type: PresentationFilterType.TEAM_FOLDER,
                folderId: filter.folderId,
                subFolderId
            });
        }
    }

    handleEmptyTrash = () => {
        const { presentations, workspaceId } = this.props;

        const trashedPresentations = presentations.filter(p => p.trashedAt && p.workspaceId === workspaceId);
        if (trashedPresentations.length === 0) {
            return;
        }

        ShowConfirmationDialog({
            title: "Are you sure you want to permanently delete all of the presentations in the trash?",
            message: "You can't undo this action.",
            acceptCallback: () => {
                PresentationLibraryController.deletePresentations(trashedPresentations.map(p => p.id));
            }
        });
    }

    handleCreateNewPresentation = () => {
        const { filter } = this.props;

        AppController.showCreatePresentationDialog({
            pane: CreatePresentationDialogPaneType.BLANK_PRESENTATION,
            state: {
                libraryFilter: filter
            }
        });
    }

    render() {
        const {
            workspaceId,
            readOnly,
            onCloseDialog,
            showCloseIcon,
            showSidebar,
            onHideSidebar,
            onSwitchType,
            sideBarWidth,
            onResizeSidebar,
            className,
            showLibraryViewSelect = true,
            isMyPresentationsOnly = false,
            filter,
            isInitialized,
            sort,
            viewType,
            onReady = () => { },
            resourceFolders,
            resources,
            presentations,
            isLoadingPresentations
        } = this.props;
        const {
            draggedItems,
        } = this.state;

        // Show skeleton when we're loading presentations (initial load or workspace switching)
        const shouldShowSkeleton = isLoadingPresentations;

        if (!isInitialized) {
            return (<UIPane>
                <Spinner />
            </UIPane>);
        }

        if (window.isZoomApp) {
            filter.type = PresentationFilterType.ALL_PRESENTATIONS;
        }

        let subFolders = [];
        let breadcrumb;
        switch (filter.type) {
            case PresentationFilters.ALL_PRESENTATIONS:
                breadcrumb = "All Presentations";
                break;
            case PresentationFilters.RECENT:
                breadcrumb = "Recent Presentations";
                break;
            case PresentationFilters.OWNED_BY_ME:
                breadcrumb = "Created by me";
                break;
            case PresentationFilters.SHARED_WITH_ME:
                breadcrumb = "Shared With Me";
                break;
            case PresentationFilters.FOLDER:
            case PresentationFilters.TEAM_FOLDER:
                let folder = resourceFolders.find(f => f.id === (filter.subFolderId || filter.folderId));
                if (folder) {
                    breadcrumb = folder.name;
                    subFolders = resourceFolders.filter(f => f.parentFolderId === folder.id);

                    while (folder.parentFolderId) {
                        folder = resourceFolders.find(f => f.id == folder.parentFolderId);
                        if (folder) {
                            breadcrumb = folder.name + " > " + breadcrumb;
                        }
                    }
                }
                break;
            case PresentationFilters.TRASH:
                breadcrumb = "Trash";
                break;
        }

        const canManageSharedSlides =
            !app.integrator &&
            !readOnly &&
            WorkspaceController.actionPermissions[ActionPermissionsObject.SHARED_RESOURCES].manage;

        const TrashHeader = filter.type == PresentationFilters.TRASH ? (
            <FlexBox middle right fillWidth style={{ paddingRight: 40 }}>
                <StyledWarningIcon>warning</StyledWarningIcon>
                <Gap10 />
                <WarningBox>
                    Items in your trash will be permanently deleted after 30 days
                </WarningBox>
                <Gap20 />
                <WarningButton
                    // @ts-ignore
                    onClick={this.handleEmptyTrash}>
                    Empty Trash
                </WarningButton>
            </FlexBox>
        ) : null;

        const canCreateResources = WorkspaceController.actionPermissions[ActionPermissionsObject.RESOURCES].create;

        return (
            // @ts-ignore
            <UIPaneWithSideBar className={className ?? ""}>
                <OnMountedReporter onMounted={onReady} />
                {!window.isZoomApp &&
                    <UIPaneSideBar
                        // @ts-ignore
                        show={showSidebar}
                        onHide={onHideSidebar}
                        style={{ width: sideBarWidth }}
                        onResize={onResizeSidebar}
                    >
                        {!app.isMobileOrTablet && showLibraryViewSelect && canCreateResources && <PresentationLibraryViewSelect libraryView={LibraryView.LIBRARY} onSwitchType={onSwitchType} isMyPresentationsOnly={isMyPresentationsOnly} />}
                        <PresentationLibrarySideBar
                            presentations={presentations}
                            filter={filter}
                            onSetFilter={this.handleSetFilter}
                            readOnly={readOnly}
                            draggedItems={draggedItems}
                            canEditLibraryItems={canManageSharedSlides}
                        />
                    </UIPaneSideBar>
                }

                {
                    !app.isMobileOrTablet &&
                    // @ts-ignore
                    <UIPaneContents style={{
                        paddingLeft: 30,
                        paddingRight: 10,
                        paddingBottom: 0
                    }}>
                        {
                            showCloseIcon &&
                            <CloseButtonContainer>
                                <Icon onClick={onCloseDialog}>
                                    close
                                </Icon>
                            </CloseButtonContainer>
                        }

                        {
                            app.integrator &&
                            <>
                                <Gap20 />
                                <CreatePresentationButtonIntegrator
                                    id="new-presentation-button"
                                    variant="text"
                                    onClick={this.handleCreateNewPresentation}
                                >
                                    <Icon>add</Icon>Create New Presentation
                                </CreatePresentationButtonIntegrator>
                            </>
                        }

                        {window.roomID && (
                            window.isZoomApp
                                ? <RemoteControlBanner blue>Select presentation to share in Zoom meeting.</RemoteControlBanner>
                                : <RemoteControlBanner>Select a presentation to Remote Play</RemoteControlBanner>
                        )}
                        {!window.roomID && window.isZoomApp && (
                            <RemoteControlBanner blue>Select a presentation to preview in player, please use the browser for full access.</RemoteControlBanner>
                        )}

                        {shouldShowSkeleton && <LibrarySkeleton /> }

                        {!shouldShowSkeleton && <>
                            <FlexBox left gap={20} middle style={{ height: 65 }}>
                                <Breadcrumb className="breadcrumb">{breadcrumb}</Breadcrumb>
                                {
                                    filter.type != PresentationFilters.TRASH &&
                                    <>
                                        <FlexSpacer />
                                        <PresentationListHeader
                                        // @ts-ignore
                                            onSearch={this.handleSearchThrottled}
                                            viewType={viewType}
                                            onSetViewType={this.handleSetViewType}
                                            sortOptions={this.sortOptions}
                                            onSetSort={this.handleSort}
                                            sort={sort}
                                            searchPlaceholder=""
                                        />
                                    </>
                                }
                                {TrashHeader}
                            </FlexBox>
                            {subFolders.length > 0 &&
                            <SubFolderContainer>
                                {filter.subFolderId &&
                                    <IconButton size="small"
                                        onMouseUp={() => this.handleSubFolderMouseUp(null)}><Icon>arrow_upward</Icon></IconButton>
                                }
                                {subFolders.map(subFolder => (
                                    <SubFolder
                                        key={subFolder.id}
                                        onMouseUp={() => this.handleSubFolderMouseUp(subFolder.id)}
                                        isSelected={filter.subFolderId === subFolder.id}
                                    >
                                        <Icon>folder_open</Icon>
                                        <Gap10 />
                                        {subFolder.name}
                                        <Gap5 />
                                        <span>({resources.filter(r => r.folderIds.includes(subFolder.id)).length})</span>
                                    </SubFolder>)
                                )}
                            </SubFolderContainer>
                            }
                            <ScrollBox id="presentation_list" ref={this.scrollRef}
                                fillWidth
                                style={{ height: 0, flexGrow: 2, marginTop: 5, paddingRight: 10, position: "relative" }}
                            >
                                {this.renderItems()}
                            </ScrollBox>
                        </>}
                    </UIPaneContents>
                }
                {
                    app.isMobileOrTablet &&
                    <UIPaneContents style={{ padding: 0 }}>
                        {
                            showCloseIcon &&
                            <CloseButtonContainer>
                                <Icon onClick={onCloseDialog}>
                                    close
                                </Icon>
                            </CloseButtonContainer>
                        }

                        {window.roomID && (
                            window.isZoomApp
                                ? <RemoteControlBanner blue>Select presentation to share in Zoom meeting</RemoteControlBanner>
                                : <RemoteControlBanner>Select Presentation to Remote Play</RemoteControlBanner>
                        )}
                        {!window.roomID && window.isZoomApp && (
                            <RemoteControlBanner blue>Select a presentation to preview in meeting, please use the browser for full access.</RemoteControlBanner>
                        )}

                        {shouldShowSkeleton && <LibrarySkeleton /> }

                        {/* @ts-ignore */}
                        {!shouldShowSkeleton && <>
                            <FlexBox center middle>
                                {
                                    filter.type != PresentationFilters.TRASH &&
                                    <>
                                        <PresentationListHeader
                                        // @ts-ignore
                                            onSearch={this.handleSearchThrottled}
                                            viewType={viewType}
                                            onSetViewType={this.handleSetViewType}
                                            sortOptions={this.sortOptions}
                                            onSetSort={this.handleSort}
                                            sort={sort}
                                            searchPlaceholder=""
                                        />
                                    </>
                                }
                                {TrashHeader}
                            </FlexBox>
                            {!window.isZoomApp && // @ts-ignore
                            <FlexBox middle style={{
                                margin: 20,
                                marginTop: 0,
                                marginBottom: 0,
                            }}>
                                <Breadcrumb className="breadcrumb" style={{
                                    width: "100%",
                                    marginRight: 0,
                                }}>{breadcrumb}</Breadcrumb>
                            </FlexBox>
                            }
                            {/* @ts-ignore */}
                            <ScrollBox id="presentation_list" ref={this.scrollRef}
                                fillWidth
                                style={{ height: 0, flexGrow: 2, marginTop: 0, paddingRight: 10, paddingLeft: 10, position: "relative" }}
                            >
                                {this.renderItems()}
                                <Gap30 />
                            </ScrollBox>
                        </>}
                    </UIPaneContents>
                }
                <ShortcutPanel />
            </UIPaneWithSideBar>
        );
    }

    renderItems() {
        const { readOnly, filteredPresentations, filter, sort, viewType, searchQuery, isPerformingSearch } = this.props;

        const Content = (() => {
            if (filteredPresentations.length == 0) {
                switch (filter.type) {
                    case PresentationFilters.ALL_PRESENTATIONS:
                    case PresentationFilters.OWNED_BY_ME:
                    case PresentationFilters.RECENT:
                        //currently readOnly is set to true when viewing presentation library from CopySlidePane.
                        if (readOnly || searchQuery) {
                            return <NoMatchNotice />;
                        } else {
                            if (app.isConstrained) {
                                return (
                                    <Notice
                                        title="You have no presentations in this workspace."
                                        message="Open Beautiful.ai in your desktop browser to create one."
                                    />
                                );
                            } else {
                                if (WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.RESOURCES, PermissionActionType.CREATE)) {
                                    return (
                                        <ResourceNotice
                                            image={getStaticUrl("/images/notice-banner/library.svg")}
                                            title="There are no presentations in this workspace."
                                            message="You are a guest in this workspace. You can find presentations shared with you here."
                                        />
                                    );
                                } else if (WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.GENERATIVE_AI, PermissionActionType.CREATE)) {
                                    return (
                                        <ResourceNotice
                                            image={getStaticUrl("/images/notice-banner/library.svg")}
                                            title="You don't have any presentations yet."
                                            message={
                                                <>
                                                    <FlexBox>Create presentations in minutes with automated design.</FlexBox>
                                                    <div style={{
                                                        display: "grid",
                                                        gridTemplateColumns: "1fr 1fr",
                                                        gap: "15px",
                                                        marginTop: "10px",
                                                        backgroundColor: "#F7FAFC",
                                                        padding: "15px",
                                                        borderRadius: "2px",
                                                        justifyItems: "start"
                                                    }}>
                                                        <FlexBox>
                                                            <StyledRoundedIcon color="primary" />
                                                            <span>Create presentations in one click with AI</span>
                                                        </FlexBox>
                                                        <FlexBox>
                                                            <StyledRoundedIcon color="primary" />
                                                            <span>Hundreds of Smart Slide templates</span>
                                                        </FlexBox>
                                                        <FlexBox>
                                                            <StyledRoundedIcon color="primary" />
                                                            <span>Create your branded theme</span>
                                                        </FlexBox>
                                                        <FlexBox>
                                                            <StyledRoundedIcon color="primary" />
                                                            <span>Be a presentation rockstar, make beautiful slides in minutes</span>
                                                        </FlexBox>
                                                    </div>
                                                </>
                                            }
                                        />
                                    );
                                }
                                return (
                                    <Notice
                                        title="Click Create Presentation to get started."
                                    />
                                );
                            }
                        }
                    case PresentationFilters.SHARED_WITH_ME:
                        return (
                            <Notice
                                title="No one has shared a presentation with you."
                                message={(
                                    <>
                                        <span>Maybe you should share one of yours? </span>
                                        <br />
                                        <span>
                                            <a target="_blank"
                                                href="https://support.beautiful.ai/hc/en-us/articles/360000771231-How-can-I-collaborate-with-someone-">Learn more</a> about sharing and collaboration.
                                        </span>
                                    </>
                                )}
                            />
                        );
                    case PresentationFilters.TRASH:
                        return (
                            <Notice
                                title="Trash is empty"
                            />
                        );
                    case PresentationFilters.FOLDER:
                        return (
                            <Notice
                                title="This folder is empty."
                                message={!app.isConstrained && "You can drag presentations into this folder to add them."}
                            />
                        );
                    case PresentationFilters.TEAM_FOLDER:
                        return (
                            <ResourceNotice
                                image={getStaticUrl(`/images/notice-banner/team-folders.svg`)}
                                title="Organize your work in shared Team Folders."
                                message="Collaborate seamlessly and keep your team’s presentations organized and easy to find with Team Folders."
                            />
                        );
                    default:
                        return (
                            <Notice
                                title="No presentations found"
                            />
                        );
                }
            }

            if (viewType === "grid") {
                return (
                    <PresentationLibraryGrid
                        readOnly={readOnly}
                        onSelectedPresentations={this.handleSelectedPresentations}
                        onDoubleClick={this.handleDoubleClickedPresentation}
                        onStartDragItems={this.handleStartDragItems}
                        onDropDragItems={this.handleDropDragItems}
                    />
                );
            }

            return (
                <PresentationLibraryList
                    readOnly={readOnly}
                    onSort={this.handleSort}
                    onSelectedPresentations={this.handleSelectedPresentations}
                    onDoubleClick={this.handleDoubleClickedPresentation}
                    onStartDragItems={this.handleStartDragItems}
                    onDropDragItems={this.handleDropDragItems}
                />
            );
        })();

        return (<>
            <FetchingClickShield backgroundColor="rgb(238 238 238)" visible={isPerformingSearch} />
            {Content}
        </>);
    }
}

export const PresentationLibraryPane =
    PresentationLibraryController.withState(
        withFirebaseUser(
            AppController.withState(
                WorkspaceController.withState(
                    InnerPresentationLibraryPane
                )
            )
        )
    );
